import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Logo from '../components/ui/Logo';
import { apiService } from '../services/api';
import SEOHead from '../components/SEOHead';
import {
  TrendingUpIcon,
  CheckIcon,
  PortfolioIcon,
  TwitterIcon,
  LinkedInIcon
} from '../components/ui/Icons';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);

  const handleGetStarted = async () => {
    setIsCheckingAuth(true);

    try {
      // Check if user has valid auth token
      const token = localStorage.getItem('authToken');

      if (!token) {
        // No token, redirect to login
        navigate('/login');
        return;
      }

      // Verify if the token is still valid
      const response = await apiService.auth.verifyToken();

      if (response.success && response.data) {
        // Token is valid, redirect to dashboard
        navigate('/dashboard');
      } else {
        // Token is invalid, clear it and go to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
      }
    } catch (error: any) {
      console.error('Auth verification failed:', error);

      // If token verification fails, try to refresh the token
      if (error.response?.status === 401) {
        try {
          const refreshResponse = await apiService.auth.refreshToken();

          if (refreshResponse.success && refreshResponse.data) {
            // Token refreshed successfully, update localStorage and redirect to dashboard
            localStorage.setItem('authToken', refreshResponse.data.access_token);
            localStorage.setItem('refreshToken', refreshResponse.data.refresh_token);
            navigate('/dashboard');
            return;
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        }
      }

      // Clear invalid tokens and go to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login');
    } finally {
      setIsCheckingAuth(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <SEOHead
        title="Manage and Grow your Wealth"
        description="The all-in-one platform for modern investors. Track your portfolio across brokers and discover exclusive high-growth opportunities."
        canonicalUrl="/"
      />
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Logo size="md" />
            <div className="flex space-x-4">
              <button
                onClick={handleGetStarted}
                disabled={isCheckingAuth}
                className="bg-trade-secondary hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCheckingAuth ? 'Checking...' : 'Get Started'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            {/* Hero Logo */}
            <div className="flex justify-center mb-8">
              <img
                src="/logo-hero.png"
                alt="MarketEye Logo"
                className="h-32 w-32 object-contain drop-shadow-lg"
              />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl tracking-tight">
              Manage and Grow<br />
              <span className="text-trade-primary">your wealth</span>
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-600">
              The all-in-one platform for modern investors. Track your portfolio across brokers and discover exclusive high-growth opportunities.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={() => navigate('/grow')}
                className="bg-trade-primary hover:bg-blue-700 text-white font-medium text-lg px-8 py-3 rounded-lg transition-colors shadow-lg shadow-blue-500/30"
              >
                Explore Opportunities
              </button>
              <button
                onClick={handleGetStarted}
                className="bg-white text-slate-700 hover:bg-gray-50 border border-slate-200 font-medium text-lg px-8 py-3 rounded-lg transition-colors"
              >
                Manage Portfolio
              </button>
            </div>
          </div>
        </div>

        {/* Dual Sections: Grow & Manage */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">

              {/* Grow Section */}
              <div className="relative group rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-8 lg:p-12 text-white overflow-hidden shadow-xl">
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                    <TrendingUpIcon className="w-6 h-6 text-green-400" />
                  </div>
                  <h2 className="text-3xl font-bold mb-4">Grow Your Wealth</h2>
                  <p className="text-slate-300 mb-8 text-lg">
                    Access investment opportunities previously reserved for the ultra-wealthy.
                  </p>

                  <ul className="space-y-4 mb-8">
                    <li className="flex items-start">
                      <CheckIcon className="w-6 h-6 text-green-400 mr-3 shrink-0" />
                      <span className="text-slate-200">Buy unlisted shares of top startups / pre-IPO companies</span>
                    </li>
                  </ul>

                  <button
                    onClick={() => navigate('/grow')}
                    className="w-full sm:w-auto bg-white text-slate-900 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition-colors"
                  >
                    Start Growing
                  </button>
                </div>

                {/* Background Decor */}
                <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-green-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-full h-full bg-[url('/grid.svg')] opacity-10"></div>
              </div>

              {/* Manage Section */}
              <div className="relative group rounded-2xl bg-white border border-slate-200 p-8 lg:p-12 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                  <PortfolioIcon className="w-6 h-6 text-trade-primary" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Manage Portfolio</h2>
                <p className="text-slate-600 mb-8 text-lg">
                  Consolidate your investments and make smarter decisions with advanced tools.
                </p>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <CheckIcon className="w-6 h-6 text-trade-primary mr-3 shrink-0" />
                    <span className="text-slate-700">Track holdings across all brokers</span>
                  </li>
                  <li className="flex items-start">
                    <CheckIcon className="w-6 h-6 text-trade-primary mr-3 shrink-0" />
                    <span className="text-slate-700">Set advanced price & stop-loss alerts</span>
                  </li>
                </ul>

                <button
                  onClick={handleGetStarted}
                  className="w-full sm:w-auto bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold py-3 px-6 rounded-lg transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>

            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-trade-primary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white">
              Ready to take control of your investments?
            </h2>
            <p className="mt-4 text-xl text-gray-300">
              Invest smarter, not harder.
            </p>
            <div className="mt-8">
              <button
                onClick={handleGetStarted}
                disabled={isCheckingAuth}
                className="bg-white text-trade-primary hover:bg-gray-100 font-medium py-3 px-8 rounded-lg transition-colors duration-200 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCheckingAuth ? 'Checking...' : 'Get Started'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div>
                <h3 className="text-lg font-semibold mb-4">MarketEye</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Make and manage your investments effortlessly.
                </p>
                <div className="flex space-x-4">
                  <a href="https://twitter.com/ankitdaf" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                    <span className="sr-only">Twitter</span>
                    <TwitterIcon className="h-6 w-6" />
                  </a>
                  <a href="https://www.linkedin.com/in/ankit-d-42451012/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                    <span className="sr-only">LinkedIn</span>
                    <LinkedInIcon className="h-6 w-6" />
                  </a>
                </div>
              </div>

              {/* Product Links */}
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-4">Product</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link to="/know-more" className="text-gray-400 hover:text-white transition-colors">
                      How it Works
                    </Link>
                  </li>
                  <li>
                    <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                      Contact Us
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Support Links */}
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-4">Support</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                      Contact Us
                    </Link>
                  </li>
                  <li>
                    <Link to="/know-more" className="text-gray-400 hover:text-white transition-colors">
                      Help Center
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Legal Links */}
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-4">Legal</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                      Terms of Service
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                      Privacy Policy
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="mt-8 pt-8 border-t border-gray-800 text-center">
              <p className="text-gray-400 text-sm">
                © 2025 MarketEye. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage; 